import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useSelector } from "react-redux"
import { toast } from "sonner"
import { selectCurrentOrganizationId } from "@/modules/organizations/store/organizationSlice"
import type { RootState } from "@/store"
import { getApiErrorMessage } from "../infrastructure/api-error"
import { salaryIncreaseService } from "../infrastructure/salary-increase-service"

const PERIODS_KEY = "increase-periods"
const ISSUES_KEY = "reporting-line-issues"

function useOrganizationId() {
  return useSelector((s: RootState) => selectCurrentOrganizationId(s))
}

export function useIncreasePeriods() {
  const organizationId = useOrganizationId()

  return useQuery({
    queryKey: [PERIODS_KEY, organizationId],
    queryFn: () => salaryIncreaseService.getIncreasePeriods(organizationId!),
    enabled: !!organizationId,
  })
}

// Every fetch reads Sage 300 (reporting lines are fixed there), so this refetches when the
// user comes back to the tab and on the page's Refresh button, never from a stale cache.
export function useReportingLineIssues(periodId: string | undefined) {
  const organizationId = useOrganizationId()

  return useQuery({
    queryKey: [ISSUES_KEY, organizationId, periodId],
    queryFn: () =>
      salaryIncreaseService.getReportingLineIssues(organizationId!, periodId!),
    enabled: !!organizationId && !!periodId,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
  })
}

export function useOpenPeriodToManagers() {
  const queryClient = useQueryClient()
  const organizationId = useOrganizationId()

  return useMutation({
    mutationFn: (periodId: string) => {
      if (!organizationId) throw new Error("No organisation selected")
      return salaryIncreaseService.openPeriodToManagers(organizationId, periodId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PERIODS_KEY] })
      queryClient.invalidateQueries({ queryKey: [ISSUES_KEY] })
      toast.success("Period opened to managers")
    },
    onError: (error) => {
      // Sage may have changed since the page was loaded, so show what it says now.
      queryClient.invalidateQueries({ queryKey: [ISSUES_KEY] })
      toast.error(getApiErrorMessage(error, "Could not open the period to managers"))
    },
  })
}
